import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../../../styles/Preview.scss";
import { tryFetch, IResponse } from "../../../helpers/FetchAPI";
import { getTokenOrDefault } from "../../../helpers/Storage";
import { processPayment } from "../../../helpers/PaymentAPI";

interface Order {
  id: string;
  order_id: string;
  event_id: string;
  event_title: string;
  event_venue: string;
  ticket_type: string;
  event_start_date: string;
  event_end_date: string;
  selected_dates: string[] | null;
  ticket_count: string;
  sub_total: string;
  admin_fee: string;
  order_total: string;
  created_at: string;
  event_image: string;
  event_rating: string;
  order_reference: string;
  customer_id: string;
  ticket_type_id: string;
  status: string; 
}

const PreviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(0);
  const [agreements, setAgreements] = useState({
    privacy: false,
    terms: false,
  });
  const [isPaying, setIsPaying] = useState(false);

   const parseDate = (dateString: string) => {
    if (!dateString) return new Date();
    return new Date(dateString.replace(/-/g, "/"));
  };

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const token = getTokenOrDefault();
        const res: IResponse = await tryFetch({
          url: "/sec/ticket-orders",
          method: "GET",
          token,
        });

        if (res.is_success && Array.isArray(res.data)) {
          const foundOrder = (res.data as Order[]).find((o) => o.id === id);

          if (foundOrder) {
            setOrder(foundOrder);

            const createdAt = parseDate(foundOrder.created_at);
            const expireAt = createdAt.getTime() + 15 * 60 * 1000;
            const now = Date.now();

            const remaining = Math.max(0, Math.floor((expireAt - now) / 1000));
            setTimeLeft(remaining);
          }
        } else {
          console.error("Failed to fetch orders:", res.error);
        }
      } catch (err) {
        console.error("Error fetching order:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id]);

  const handlePayment = async () => {
    if (!id || isPaying) return;
    setIsPaying(true);

     try {
        if (Number(order?.order_total) === 0) {
          navigate(`/transactions`);
          return;
       }

      const res = await processPayment(id);

      if (res.is_success && res.data && typeof res.data === "object") {
        const paymentData = res.data as { payment_url?: string };
        if (paymentData.payment_url) {
          window.location.href = paymentData.payment_url;
          return;
        }
      }
      console.error("Payment failed:", res.error || "No payment_url in response");
    } catch (err) {
      console.error("Payment error:", err);
    } finally {
      setIsPaying(false);
    }
  };


  useEffect(() => {
    if (timeLeft <= 0) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          if (order) {
            navigate(`/event/${order.event_id}/get-ticket`);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [timeLeft, order, navigate]);

   const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const allChecked = agreements.privacy && agreements.terms;

   const dateCreatedFormatter = new Intl.DateTimeFormat("en-PH", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Manila",
  });

  if (loading) return <div>Loading...</div>;
  if (!order) return <div>No order found. Please check your order ID.</div>;

  return (
    <div className="preview-page">
      <h2>CHECKOUT PREVIEW</h2>
      <hr />
      <div className="header-info">
        <p>
          <strong>Reference No.:</strong> {order.order_reference}
        </p>
         <p className="timer">{formatTime(timeLeft)}</p>
       <p>
           <strong>Date Created:</strong>{" "}
          {order.created_at
            ? dateCreatedFormatter.format(parseDate(order.created_at))
            : "N/A"}
        </p>
        {/* <p className="timer">{formatTime(timeLeft)}</p> */}
      </div>
      <hr />

      {/* <div className="p-event-details"> */}
        <div className="preview-content">
        <div className="left">
        <img src={order.event_image} alt={order.event_title} className="preview-img" />
        </div>
        <div className="right">
        <h2>{order.event_title}</h2>
        <p>Venue: {order.event_venue}</p>
        <p>
          Date and Time:{" "}
          {order.event_start_date
            ? new Intl.DateTimeFormat("en-PH", {
                dateStyle: "medium",
                timeStyle: "short",
                timeZone: "Asia/Manila",
              }).format(parseDate(order.event_start_date))
            : "N/A"}
        </p>
        <p>Ticket Type: {order.ticket_type}</p>
      <hr />

      <div className="order-summary">
        <p>
          <span>Quantity:</span>
          <span>{order.ticket_count}</span>
        </p>
        <p>
          <span>Subtotal:</span>
          <span>₱{Number(order.sub_total).toLocaleString()}</span>
        </p>
        <p>
          <span> Convenience Fee:</span>
          <span>₱{Number(order.admin_fee).toLocaleString()}</span>
        </p>
      </div>
      <hr />

      <div className="total-amount">
        <strong>TOTAL AMOUNT TO PAY:</strong>
        <span>₱{Number(order.order_total).toLocaleString()}</span>
      </div>

      <p className="note">
        Note: Please pay the amount to secure your ticket before the time runs out.
      </p>

      <div className="agreements">
        <label>
          <input
            type="checkbox"
            checked={agreements.privacy}
            onChange={(e) =>
              setAgreements({ ...agreements, privacy: e.target.checked })
            }
          />
           I have read, understood, and agree to be bound by these Terms and Conditions.
        </label>
        <label>
          <input
            type="checkbox"
            checked={agreements.terms}
            onChange={(e) =>
              setAgreements({ ...agreements, terms: e.target.checked })
            }
          />
          Terms, Conditions and Policies apply. <br /> Please contact the organizer
          for more details.
        </label>
      </div>

      {timeLeft > 0 ? (
        <button
          className="proceed-btn"
          disabled={!allChecked}
          onClick={handlePayment}
        >
          PAY NOW
        </button>
      ) : (
        <button
          className="proceed-btn"
          onClick={() => navigate(`/event/${order.event_id}/get-ticket`)}
        >
          GET TICKET
        </button>
      )}
    </div>
    </div>
    </div>
  );
};

export default PreviewPage;
